import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import {
  DataActionConditionType,
  DATA_ACTION_CONDITION_TYPE_LABEL,
  CreateEventComposerSection,
} from 'cio/utils/campaigns/data-action';
import { keyPathToString, stringToPath } from 'cio/utils/objects';
import { ActionType } from '../../models/action';
import { htmlSafe } from '@ember/template';
import { SafeString } from 'handlebars';

interface BaseAPIBodyRow {
  name: string;
  type: DataActionConditionType;
}

interface APIBodyRowTriggerAttribute extends BaseAPIBodyRow {
  type: DataActionConditionType.TriggerAttribute;
  value?: string;
  path?: (string | number)[];
}

interface APIBodyRowProfileAttribute extends BaseAPIBodyRow {
  type: DataActionConditionType.ProfileAttribute;
  value?: string;
  path?: (string | number)[];
}

interface APIBodyRowEventAttribute extends BaseAPIBodyRow {
  type: DataActionConditionType.EventAttribute;
  value?: string;
  path?: (string | number)[];
}

interface APIBodyRowValue extends BaseAPIBodyRow {
  type: DataActionConditionType.Static;
  value: string;
}

interface APIBodyRowLiquid extends BaseAPIBodyRow {
  type: DataActionConditionType.Liquid;
  value: string;
}

interface APIBodyRowRemoveAttribute extends BaseAPIBodyRow {
  type: DataActionConditionType.RemoveAttribute;
  value: string;
}

interface APIBodyRowJavaScript extends BaseAPIBodyRow {
  type: DataActionConditionType.JavaScript;
  value: string;
}

export type APIBodyRow =
  | APIBodyRowTriggerAttribute
  | APIBodyRowProfileAttribute
  | APIBodyRowEventAttribute
  | APIBodyRowValue
  | APIBodyRowLiquid
  | APIBodyRowRemoveAttribute
  | APIBodyRowJavaScript;

export default class DataActionBodyAttributeRow implements CreateEventComposerSection {
  actionType: ActionType | null = null;

  @tracked name = '';
  @tracked type: DataActionConditionType = DataActionConditionType.TriggerAttribute;
  @tracked value = '';
  @tracked path: (string | number)[] = [];

  originalName = '';
  originalType = DataActionConditionType.TriggerAttribute;
  originalValue = '';

  constructor(options?: {
    apiRow?: APIBodyRow | null;
    type?: DataActionConditionType;
    actionType?: ActionType | null;
  }) {
    if (options?.actionType != null) {
      this.actionType = options.actionType;
    }

    if (options?.type != null) {
      this.type = options.type;
    }

    if (options?.apiRow != null) {
      let { apiRow } = options;
      this.name = apiRow.name;
      this.type = apiRow.type;

      if (options?.actionType === 'attribute_update_action' && isEmpty(apiRow.value)) {
        // If any condition value is empty in a "create or update person"
        // action, quietly convert it to a "Remove attribute" condition.
        // We are adding validation to prevent saving empty values in these
        // actions, but this is necessary for backwards compatibility.
        this.type = DataActionConditionType.RemoveAttribute;
        this.value = '';
      } else {
        if (
          apiRow.type === DataActionConditionType.Static ||
          apiRow.type === DataActionConditionType.Liquid ||
          apiRow.type === DataActionConditionType.JavaScript
        ) {
          this.value = apiRow.value;
        } else if (apiRow.type === DataActionConditionType.RemoveAttribute) {
          this.value = '';
        } else {
          if (apiRow.value != null) {
            this.path = stringToPath(apiRow.value);
            this.value = apiRow.value;
          } else if (apiRow.path != null) {
            this.path = apiRow.path;
            this.value = keyPathToString(this.path);
          }
        }
      }
    }

    this.originalName = this.name;
    this.originalType = this.type;
    this.originalValue = this.value;
  }

  get isDirty() {
    return this.name !== this.originalName || this.type !== this.originalType || this.value !== this.originalValue;
  }

  get hasIllegalEmptyValue() {
    return (
      this.actionType === 'attribute_update_action' &&
      isEmpty(this.value) &&
      this.type !== DataActionConditionType.RemoveAttribute
    );
  }

  get hasErrors(): boolean {
    return Object.keys(this.errors).length > 0;
  }

  get errors(): Record<string, (string | SafeString)[]> {
    let errors: Record<string, (string | SafeString)[]> = {};

    if (!isEmpty(this.value) && isEmpty(this.name.trim())) {
      errors.name = ["Name can't be blank"];
    }

    if (this.hasIllegalEmptyValue) {
      errors.value = [
        htmlSafe(
          `Enter a value, or use the <b>${
            DATA_ACTION_CONDITION_TYPE_LABEL[DataActionConditionType.RemoveAttribute]
          }</b> value type`,
        ),
      ];
    }

    return errors;
  }
}
